import { useEffect, useState, useCallback } from "react";
import {
  FailedNotification,
  FailedNotificationResponse,
  UseNotificationDropdownReturn,
} from "../types/Notification.type";
import { getFailedNotifications } from "../services/notificationService";

export const useNotificationDropdown = (
  enabled = true,
): UseNotificationDropdownReturn => {
  const [notifications, setNotifications] = useState<FailedNotification[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    if (!enabled) {
      return;
    }

    setError(null);

    try {
      const response = await getFailedNotifications();

      if (!response.success) {
        setError(response.message || "Failed to fetch notifications");
        setNotifications([]);
        setTotalCount(0);
        return;
      }

      const data = response.data as FailedNotificationResponse["data"];
      const last3 = data.data.slice(0, 3);
      setNotifications(last3);
      setTotalCount(data.totalCount);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(errMsg);
      setNotifications([]);
      setTotalCount(0);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const timer = window.setTimeout(() => {
      void fetchNotifications();
    }, 0);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [enabled, fetchNotifications]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleRefresh = (): void => {
      void fetchNotifications();
    };
    window.addEventListener("notification:acknowledged", handleRefresh);
    return (): void => {
      window.removeEventListener("notification:acknowledged", handleRefresh);
    };
  }, [enabled, fetchNotifications]);

  const refreshNotifications = useCallback((): void => {
    void fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    totalCount,
    error,
    refreshNotifications,
  };
};
